import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useActiveClinic } from "../../../context/ClinicContext"
import { useAuth } from "../../../context/AuthContext"

import { ui } from "../../../shared/styles/uiSystem"
import PageHero from "../../../shared/components/PageHero"
import PillTabs from "../../../shared/components/PillTabs"

import { useClinicPlanSnapshot } from "../../subscriptions/hooks/useClinicPlanSnapshot"

import DashboardContainer from "../containers/DashboardContainer"
import DashboardExecutiveFeedSection from "../components/DashboardExecutiveFeedSection"
import GlobalExecutiveSection from "../components/GlobalExecutiveSection"
import SingleClinicExecutiveSection from "../components/SingleClinicExecutiveSection"
import OperativeAdvancedSection from "../components/OperativeAdvancedSection"

import {
  openDashboardActionTarget,
  openFinancialActionRoute
} from "../controllers/dashboardNavigation"

import useDashboardViewModel from "../hooks/useDashboardViewModel"
import useDashboardDataLoader from "../hooks/useDashboardDataLoader"

import {
  formatMoney,
  formatPct,
  getHeatColor,
  getSatColor,
  getSatBg,
  getSatLabel,
  getScoreBadge,
  getActionCardStyle
} from "../utils/dashboardPresentation"

function getTodayLocal() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, "0")
  const d = String(now.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export default function DashboardPro() {
  const { activeClinicId, clinicOptions } = useActiveClinic()
  const { user } = useAuth()
  const navigate = useNavigate()

  const storedClinicId = localStorage.getItem("odontoflow_clinic_id") || ""
  const snapshotClinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : storedClinicId && storedClinicId !== "ALL"
        ? storedClinicId
        : null

  const { data: planSnapshot } = useClinicPlanSnapshot(snapshotClinicId)

  const [date, setDate] = useState(getTodayLocal())
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  const [dashboardMode, setDashboardMode] = useState(
    localStorage.getItem("odontoflow_dashboard_mode") || "EXECUTIVE"
  )

  const [dashboardFocus, setDashboardFocus] = useState(() => {
    if (typeof window === "undefined") return ""

    const params = new URLSearchParams(window.location.search)
    return (
      params.get("focus") ||
      window.localStorage.getItem("odontoflow_dashboard_focus") ||
      ""
    )
  })

  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem("odontoflow_dashboard_collapsed")
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  })

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

    useEffect(() => {
    if (typeof window === "undefined") return

    const params = new URLSearchParams(window.location.search)
    const focusFromUrl = params.get("focus")
    const sourceFromUrl = params.get("source")

    const storedFocus = window.localStorage.getItem("odontoflow_dashboard_focus")
    const storedSource = window.localStorage.getItem("odontoflow_dashboard_source")

    const nextFocus = focusFromUrl || storedFocus || ""
    const source = sourceFromUrl || storedSource || ""

    if (nextFocus === "agenda-actions" || source === "agenda") {
      setDashboardFocus("agenda-actions")
      setDashboardMode("OPERATIVE")
      window.localStorage.setItem("odontoflow_dashboard_mode", "OPERATIVE")

      setCollapsedSections((prev) => {
        const next = {
          ...prev,
          executiveAlerts: false,
          executiveActions: false,
          operativeAdvanced: false,
          saturation: false,
          heatmap: false,
        }

        window.localStorage.setItem(
          "odontoflow_dashboard_collapsed",
          JSON.stringify(next),
        )

        return next
      })
    }
  }, [])

  const isMobile = viewportWidth < 780
  const isTablet = viewportWidth >= 780 && viewportWidth < 1180

  const clinicId = activeClinicId || ""
  const isGlobal = clinicId === "ALL"

  const selectedClinicName = useMemo(() => {
    if (clinicId === "ALL") return "Todas las clínicas"
    const found = clinicOptions.find((c) => String(c.id) === String(clinicId))
    return found?.name || "Panel ejecutivo"
  }, [clinicId, clinicOptions])

  const plan = planSnapshot?.plan || null
  const currentPlanName = plan?.name || "Tu plan actual"
  const canExecutive = Boolean(plan?.allowExecutiveDashboard)
  const canAdvancedReports = Boolean(plan?.allowAdvancedReports)
  const canMultiClinic = Boolean(plan?.allowMultiClinic)

  const {
    loading,
    bundle,
    heatmap,
    saturation,
    doctorRanking,
    treatmentRanking,
    weeklyForecast,
    monthlyForecast,
    financialAlerts,
    executiveActions,
    executiveScore,
    featureLocks
  } = useDashboardDataLoader({
    clinicId,
    date,
    userId: user?.id,
    plan,
    canAdvancedReports,
    canMultiClinic,
    canExecutive
  })

  const multiClinicFeatureLocked = Boolean(featureLocks.multiClinic)
  const executiveDashboardFeatureLocked = Boolean(featureLocks.executiveDashboard)
  const advancedReportsFeatureLocked = Boolean(featureLocks.advancedReports)

  const effectiveCanExecutive = canExecutive && !executiveDashboardFeatureLocked
  const effectiveCanAdvancedReports =
    canAdvancedReports && !advancedReportsFeatureLocked

  const executiveFeedsEnabled =
    canMultiClinic &&
    canExecutive &&
    !multiClinicFeatureLocked &&
    !executiveDashboardFeatureLocked

  const executiveFeedsFeatureKey =
    !canMultiClinic || multiClinicFeatureLocked
      ? "multiClinic"
      : "executiveDashboard"

  const multiClinicExecutiveEnabled =
    canMultiClinic &&
    canExecutive &&
    !multiClinicFeatureLocked &&
    !executiveDashboardFeatureLocked

  const multiClinicExecutiveFeatureKey =
    !canMultiClinic || multiClinicFeatureLocked
      ? "multiClinic"
      : "executiveDashboard"

  const isExecutiveMode = dashboardMode === "EXECUTIVE"
  const isOperativeMode = dashboardMode === "OPERATIVE"

  const dashboardModeItems = [
    { value: "EXECUTIVE", label: "Ejecutivo" },
    { value: "OPERATIVE", label: "Operativo" }
  ]

  const setMode = useCallback((mode) => {
    setDashboardMode(mode)
    localStorage.setItem("odontoflow_dashboard_mode", mode)
  }, [])

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections((prev) => {
      const next = {
        ...prev,
        [sectionKey]: !prev?.[sectionKey]
      }

      localStorage.setItem(
        "odontoflow_dashboard_collapsed",
        JSON.stringify(next)
      )

      return next
    })
  }, [])

  const openUpgrade = useCallback(
    (featureKey) => {
      const params = new URLSearchParams()

      if (featureKey) {
        params.set("feature", featureKey)
      }

      navigate(`/upgrade-plan${params.toString() ? `?${params.toString()}` : ""}`)
    },
    [navigate]
  )

  const openFinancialAction = useCallback(
    (target, targetClinicId = clinicId) => {
      openFinancialActionRoute({
        navigate,
        target,
        targetClinicId,
        fallbackClinicId: clinicId
      })
    },
    [clinicId, navigate]
  )

  const openActionTarget = useCallback(
    (action) => {
      openDashboardActionTarget({
        action,
        navigate,
        clinicId,
        date,
        openFinancialAction
      })
    },
    [clinicId, date, navigate, openFinancialAction]
  )

  const {
    summary,
    executiveRows,
    insights,
    highlights,
    topFinancialAlerts,
    topExecutiveActions,
    filteredExecutiveScore,
    singleClinicScoreRow,
    saturatedDoctorsCount,
    lowLoadDoctorsCount,
    singleClinicReadings
  } = useDashboardViewModel({
    bundle,
    clinicId,
    financialAlerts,
    executiveActions,
    executiveScore,
    saturation,
    weeklyForecast
  })

  const heroBackground = isExecutiveMode
    ? "linear-gradient(135deg, #0f172a 0%, #1e3a8a 52%, #2563eb 100%)"
    : "linear-gradient(135deg, #0f172a 0%, #0f766e 52%, #14b8a6 100%)"

  const heroSubtitle = isExecutiveMode
    ? selectedClinicName
    : selectedClinicName

  const dashboardHero = (
    <PageHero
      eyebrow="Operación"
      title="Dashboard"
      subtitle={heroSubtitle}
      background={heroBackground}
      actions={
        <>
          <PillTabs
            items={dashboardModeItems}
            value={dashboardMode}
            onChange={setMode}
          />

          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              ...ui.input,
              width: "auto",
              minWidth: 148,
              fontWeight: 700,
              background: "rgba(255,255,255,0.96)"
            }}
          />
        </>
      }
      metrics={
        isExecutiveMode
          ? [
              {
                label: "Producción",
                value: `$${formatMoney(summary.production)}`,
                helper: "Resultado del día"
              },
              {
                label: "Cobrado",
                value: `$${formatMoney(summary.collected)}`,
                helper: "Cobranza actual"
              },
              {
                label: "Pendiente",
                value: `$${formatMoney(summary.pending)}`,
                helper: "Cartera actual"
              },
              {
                label: "Cobro",
                value: `${formatPct(summary.collectionRate)}%`,
                helper: "Eficiencia del día"
              }
            ]
          : [
              {
                label: "Citas",
                value: formatMoney(summary.appointments),
                helper: "Agenda del día"
              },
              {
                label: "Ocupación",
                value: `${formatPct(summary.avgOccupation)}%`,
                helper: "Carga operativa"
              },
              {
                label: "Presión alta",
                value: String(saturatedDoctorsCount),
                helper: "Doctores exigidos"
              },
              {
                label: "Baja carga",
                value: String(lowLoadDoctorsCount),
                helper: "Espacio recuperable"
              }
            ]
      }
    />
  )

  const agendaFocusActive = dashboardFocus === "agenda-actions"

  const hasExecutiveFeedContent =
    (topFinancialAlerts?.length || 0) > 0 ||
    (topExecutiveActions?.length || 0) > 0

  const shouldRenderExecutiveFeed =
    agendaFocusActive || !executiveFeedsEnabled || loading || hasExecutiveFeedContent

  const dashboardBanners = null

  return (
    <DashboardContainer
      hero={dashboardHero}
      kpis={null}
      banners={dashboardBanners}
    >
      {agendaFocusActive ? (
        <AgendaDashboardFocusBanner
          loading={loading}
          topExecutiveActions={topExecutiveActions}
          topFinancialAlerts={topFinancialAlerts}
          setMode={setMode}
          navigate={navigate}
          onDismiss={() => {
            setDashboardFocus("")
            if (typeof window !== "undefined") {
              window.localStorage.removeItem("odontoflow_dashboard_focus")
              window.localStorage.removeItem("odontoflow_dashboard_source")

              const params = new URLSearchParams(window.location.search)
              params.delete("focus")
              params.delete("source")

              const nextSearch = params.toString()
              const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`
              window.history.replaceState({}, "", nextUrl)
            }
          }}
        />
      ) : null}

      {!isGlobal && (isOperativeMode || agendaFocusActive) ? (
        <DashboardAgendaOperationsCenter
          loading={loading}
          summary={summary}
          topExecutiveActions={topExecutiveActions}
          topFinancialAlerts={topFinancialAlerts}
          saturatedDoctorsCount={saturatedDoctorsCount}
          lowLoadDoctorsCount={lowLoadDoctorsCount}
          agendaFocusActive={agendaFocusActive}
          isMobile={isMobile}
          navigate={navigate}
          openActionTarget={openActionTarget}
          openFinancialAction={openFinancialAction}
          setMode={setMode}
        />
      ) : null}

      {shouldRenderExecutiveFeed && (
        <DashboardExecutiveFeedSection
          executiveFeedsEnabled={executiveFeedsEnabled}
          executiveFeedsFeatureKey={executiveFeedsFeatureKey}
          currentPlanName={currentPlanName}
          openUpgrade={openUpgrade}
          isMobile={isMobile}
          loading={loading}
          topFinancialAlerts={topFinancialAlerts}
          topExecutiveActions={topExecutiveActions}
          collapsedSections={collapsedSections}
          toggleSection={toggleSection}
          openFinancialAction={openFinancialAction}
          openActionTarget={openActionTarget}
          getActionCardStyle={getActionCardStyle}
        />
      )}

      {isExecutiveMode && (
        <>
          {isGlobal ? (
            <GlobalExecutiveSection
              multiClinicExecutiveEnabled={multiClinicExecutiveEnabled}
              multiClinicExecutiveFeatureKey={multiClinicExecutiveFeatureKey}
              currentPlanName={currentPlanName}
              openUpgrade={openUpgrade}
              collapsedSections={collapsedSections}
              toggleSection={toggleSection}
              filteredExecutiveScore={filteredExecutiveScore}
              loading={loading}
              getScoreBadge={getScoreBadge}
              executiveRows={executiveRows}
              isTablet={isTablet}
              isMobile={isMobile}
              highlights={highlights}
              insights={insights}
              effectiveCanAdvancedReports={effectiveCanAdvancedReports}
              formatMoney={formatMoney}
              formatPct={formatPct}
            />
          ) : (
            <SingleClinicExecutiveSection
              effectiveCanExecutive={effectiveCanExecutive}
              currentPlanName={currentPlanName}
              openUpgrade={openUpgrade}
              singleClinicScoreRow={singleClinicScoreRow}
              getScoreBadge={getScoreBadge}
              summary={summary}
              formatMoney={formatMoney}
              formatPct={formatPct}
              effectiveCanAdvancedReports={effectiveCanAdvancedReports}
              collapsedSections={collapsedSections}
              toggleSection={toggleSection}
              singleClinicReadings={singleClinicReadings}
              loading={loading}
            />
          )}
        </>
      )}

      {!isGlobal && isOperativeMode && (
        <OperativeAdvancedSection
          effectiveCanAdvancedReports={effectiveCanAdvancedReports}
          currentPlanName={currentPlanName}
          openUpgrade={openUpgrade}
          isMobile={isMobile}
          collapsedSections={collapsedSections}
          toggleSection={toggleSection}
          loading={loading}
          doctorRanking={doctorRanking}
          treatmentRanking={treatmentRanking}
          weeklyForecast={weeklyForecast}
          monthlyForecast={monthlyForecast}
          saturation={saturation}
          heatmap={heatmap}
          getSatBg={getSatBg}
          getSatColor={getSatColor}
          getSatLabel={getSatLabel}
          getHeatColor={getHeatColor}
          formatMoney={formatMoney}
          formatPct={formatPct}
        />
      )}
    </DashboardContainer>
  )
  function AgendaDashboardFocusBanner({
  loading,
  topExecutiveActions,
  topFinancialAlerts,
  setMode,
  navigate,
  onDismiss,
}) {
  const actionsCount = topExecutiveActions?.length || 0
  const alertsCount = topFinancialAlerts?.length || 0
  const totalCount = actionsCount + alertsCount

  const message = loading
    ? "Estamos cargando las acciones relacionadas con tu agenda."
    : totalCount > 0
      ? `Encontramos ${totalCount} elemento${totalCount === 1 ? "" : "s"} para revisar: ${actionsCount} acción${actionsCount === 1 ? "" : "es"} y ${alertsCount} alerta${alertsCount === 1 ? "" : "s"}.`
      : "No hay acciones críticas en este momento, pero dejamos abierto el modo operativo para revisar ocupación, saturación y oportunidades."

  return (
    <div
      style={{
        marginBottom: 14,
        padding: 14,
        borderRadius: 20,
        background:
          "linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(236,253,245,0.96) 100%)",
        border: "1px solid #bfdbfe",
        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.07)",
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 9px",
              borderRadius: 999,
              background: "#dbeafe",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontSize: 11,
              fontWeight: 950,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
          >
            Desde Agenda
          </div>

          <div
            style={{
              fontSize: 18,
              fontWeight: 950,
              color: "#0f172a",
              letterSpacing: "-0.035em",
              lineHeight: 1.12,
            }}
          >
            Revisión operativa sugerida
          </div>

          <div
            style={{
              marginTop: 5,
              fontSize: 13,
              fontWeight: 750,
              color: "#475569",
              lineHeight: 1.45,
            }}
          >
            {message}
          </div>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          style={{
            width: 34,
            height: 34,
            borderRadius: 12,
            border: "1px solid #dbe4f0",
            background: "#ffffff",
            color: "#64748b",
            fontSize: 18,
            fontWeight: 900,
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
          }}
          title="Cerrar aviso"
        >
          ×
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => setMode("OPERATIVE")}
          style={{
            ...ui.primaryButton,
            minHeight: 38,
          }}
        >
          Ver operación
        </button>

        <button
          type="button"
          onClick={() => setMode("EXECUTIVE")}
          style={{
            ...ui.secondaryButton,
            minHeight: 38,
          }}
        >
          Ver ejecutivo
        </button>

        <button
          type="button"
          onClick={() => navigate("/agenda")}
          style={{
            ...ui.secondaryButton,
            minHeight: 38,
          }}
        >
          Volver a Agenda
        </button>
      </div>
    </div>
  )
}

function DashboardAgendaOperationsCenter({
  loading,
  summary,
  topExecutiveActions,
  topFinancialAlerts,
  saturatedDoctorsCount,
  lowLoadDoctorsCount,
  agendaFocusActive,
  isMobile,
  navigate,
  openActionTarget,
  openFinancialAction,
  setMode,
}) {
  const actions = Array.isArray(topExecutiveActions) ? topExecutiveActions : []
  const alerts = Array.isArray(topFinancialAlerts) ? topFinancialAlerts : []

  const visibleActions = actions.slice(0, 4)
  const visibleAlerts = alerts.slice(0, 3)

  const hasWork = actions.length > 0 || alerts.length > 0

  return (
    <section
      id="agenda-actions"
      style={{
        marginBottom: 16,
        padding: isMobile ? 12 : 14,
        borderRadius: 22,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
        border: "1px solid #e2e8f0",
        boxShadow: "0 18px 42px rgba(15, 23, 42, 0.065)",
        display: "grid",
        gap: 14,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) auto",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 9px",
              borderRadius: 999,
              background: agendaFocusActive ? "#dbeafe" : "#f8fafc",
              border: agendaFocusActive ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
              color: agendaFocusActive ? "#1d4ed8" : "#475569",
              fontSize: 11,
              fontWeight: 950,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
          >
            Centro operativo de agenda
          </div>

          <div
            style={{
              fontSize: 20,
              fontWeight: 950,
              color: "#0f172a",
              letterSpacing: "-0.04em",
              lineHeight: 1.1,
            }}
          >
            Acciones, alertas y capacidad del día
          </div>

          <div
            style={{
              marginTop: 5,
              color: "#64748b",
              fontSize: 13,
              fontWeight: 750,
              lineHeight: 1.45,
              maxWidth: 820,
            }}
          >
            Aquí vive la lectura que antes saturaba la Agenda: acciones sugeridas,
            oportunidades, alertas financieras y presión operativa.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: isMobile ? "stretch" : "flex-end",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/agenda")}
            style={{
              ...ui.secondaryButton,
              minHeight: 38,
              width: isMobile ? "100%" : "auto",
              justifyContent: "center",
            }}
          >
            Volver a Agenda
          </button>

          <button
            type="button"
            onClick={() => setMode("OPERATIVE")}
            style={{
              ...ui.primaryButton,
              minHeight: 38,
              width: isMobile ? "100%" : "auto",
              justifyContent: "center",
            }}
          >
            Modo operativo
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr 1fr"
            : "repeat(4, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <AgendaOpsMetric
          label="Citas"
          value={formatMoney(summary?.appointments || 0)}
          helper="Agenda del día"
          tone="blue"
        />
        <AgendaOpsMetric
          label="Ocupación"
          value={`${formatPct(summary?.avgOccupation || 0)}%`}
          helper="Carga operativa"
          tone="teal"
        />
        <AgendaOpsMetric
          label="Pendiente"
          value={`$${formatMoney(summary?.pending || 0)}`}
          helper="Dinero por cobrar"
          tone="orange"
        />
        <AgendaOpsMetric
          label="Acciones"
          value={loading ? "…" : String(actions.length + alerts.length)}
          helper="Por revisar"
          tone="violet"
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.15fr) minmax(0, 0.85fr)",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "grid",
            gap: 10,
          }}
        >
          <DashboardOpsPanel
            title="Acciones sugeridas"
            subtitle="Seguimientos operativos derivados de agenda, saturación o cobranza."
            emptyText={
              loading
                ? "Cargando acciones..."
                : "Sin acciones críticas detectadas para este corte."
            }
          >
            {visibleActions.map((action, index) => (
              <DashboardOpsItem
                key={action?.id || action?.key || `${getItemTitle(action, "Acción")}-${index}`}
                item={action}
                fallbackTitle={`Acción sugerida ${index + 1}`}
                buttonLabel="Abrir"
                onClick={() => openActionTarget(action)}
              />
            ))}
          </DashboardOpsPanel>

          <DashboardOpsPanel
            title="Alertas"
            subtitle="Riesgos financieros u operativos que conviene revisar."
            emptyText={
              loading
                ? "Cargando alertas..."
                : "Sin alertas relevantes detectadas."
            }
          >
            {visibleAlerts.map((alert, index) => (
              <DashboardOpsItem
                key={alert?.id || alert?.key || `${getItemTitle(alert, "Alerta")}-${index}`}
                item={alert}
                fallbackTitle={`Alerta ${index + 1}`}
                buttonLabel="Revisar"
                onClick={() => {
                  const target =
                    alert?.target ||
                    alert?.actionTarget ||
                    alert?.routeTarget ||
                    alert?.type ||
                    "PENDING"
                  const targetClinicId =
                    alert?.clinicId || alert?.clinic?.id || alert?.accountClinicId

                  openFinancialAction(target, targetClinicId)
                }}
              />
            ))}
          </DashboardOpsPanel>
        </div>

        <div
          style={{
            display: "grid",
            gap: 10,
            alignContent: "start",
          }}
        >
          <div
            style={{
              padding: 13,
              borderRadius: 18,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              display: "grid",
              gap: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 950,
                  color: "#0f172a",
                  letterSpacing: "-0.025em",
                }}
              >
                Dinero en juego
              </div>

              <div
                style={{
                  color: "#64748b",
                  fontSize: 12,
                  fontWeight: 750,
                  marginTop: 3,
                  lineHeight: 1.35,
                }}
              >
                Lectura rápida de producción, cobro y cartera.
              </div>
            </div>

            <MoneyLine
              label="Producción"
              value={`$${formatMoney(summary?.production || 0)}`}
              tone="#1d4ed8"
            />
            <MoneyLine
              label="Cobrado"
              value={`$${formatMoney(summary?.collected || 0)}`}
              tone="#166534"
            />
            <MoneyLine
              label="Pendiente"
              value={`$${formatMoney(summary?.pending || 0)}`}
              tone="#c2410c"
            />
            <MoneyLine
              label="Eficiencia de cobro"
              value={`${formatPct(summary?.collectionRate || 0)}%`}
              tone="#4c1d95"
            />
          </div>

          <div
            style={{
              padding: 13,
              borderRadius: 18,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              display: "grid",
              gap: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 950,
                  color: "#0f172a",
                  letterSpacing: "-0.025em",
                }}
              >
                Capacidad
              </div>

              <div
                style={{
                  color: "#64748b",
                  fontSize: 12,
                  fontWeight: 750,
                  marginTop: 3,
                  lineHeight: 1.35,
                }}
              >
                Presión operativa y espacios recuperables.
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <CapacityMiniCard
                label="Presión alta"
                value={saturatedDoctorsCount || 0}
                tone="danger"
              />
              <CapacityMiniCard
                label="Baja carga"
                value={lowLoadDoctorsCount || 0}
                tone="success"
              />
            </div>

            <button
              type="button"
              onClick={() => setMode("OPERATIVE")}
              style={{
                ...ui.secondaryButton,
                width: "100%",
                justifyContent: "center",
                minHeight: 38,
              }}
            >
              Ver detalle operativo
            </button>
          </div>
        </div>
      </div>

      {!hasWork && !loading ? (
        <div
          style={{
            padding: 12,
            borderRadius: 16,
            background: "#ecfeff",
            border: "1px solid #a5f3fc",
            color: "#0f766e",
            fontSize: 13,
            fontWeight: 850,
            lineHeight: 1.4,
          }}
        >
          No hay acciones críticas por ahora. La operación se ve estable para este
          corte; puedes seguir revisando ocupación, agenda y cobranza en este mismo
          panel.
        </div>
      ) : null}
    </section>
  )
}

function AgendaOpsMetric({ label, value, helper, tone = "blue" }) {
  const tones = {
    blue: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
    teal: {
      bg: "#ecfeff",
      border: "#a5f3fc",
      color: "#0f766e",
    },
    orange: {
      bg: "#fff7ed",
      border: "#fed7aa",
      color: "#c2410c",
    },
    violet: {
      bg: "#f5f3ff",
      border: "#ddd6fe",
      color: "#6d28d9",
    },
  }

  const selected = tones[tone] || tones.blue

  return (
    <div
      style={{
        minHeight: 86,
        padding: 12,
        borderRadius: 18,
        background: selected.bg,
        border: `1px solid ${selected.border}`,
        display: "grid",
        alignContent: "center",
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          color: "#64748b",
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          fontWeight: 950,
          color: selected.color,
          letterSpacing: "-0.045em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>

      <div
        style={{
          fontSize: 11,
          fontWeight: 750,
          color: "#64748b",
          lineHeight: 1.25,
        }}
      >
        {helper}
      </div>
    </div>
  )
}

function DashboardOpsPanel({ title, subtitle, emptyText, children }) {
  const hasChildren = React.Children.count(children) > 0

  return (
    <div
      style={{
        padding: 13,
        borderRadius: 18,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        display: "grid",
        gap: 10,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 950,
            color: "#0f172a",
            letterSpacing: "-0.025em",
          }}
        >
          {title}
        </div>

        <div
          style={{
            color: "#64748b",
            fontSize: 12,
            fontWeight: 750,
            marginTop: 3,
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </div>
      </div>

      {hasChildren ? (
        <div style={{ display: "grid", gap: 8 }}>{children}</div>
      ) : (
        <div
          style={{
            padding: 12,
            borderRadius: 14,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 800,
            lineHeight: 1.35,
          }}
        >
          {emptyText}
        </div>
      )}
    </div>
  )
}

function DashboardOpsItem({
  item,
  fallbackTitle,
  buttonLabel,
  onClick,
}) {
  const title = getItemTitle(item, fallbackTitle)
  const description = getItemDescription(item)
  const tone = getItemTone(item)

  return (
    <div
      style={{
        padding: 11,
        borderRadius: 15,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 10,
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 950,
            color: tone.color,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={title}
        >
          {title}
        </div>

        {description ? (
          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              fontWeight: 750,
              color: "#475569",
              lineHeight: 1.35,
            }}
          >
            {description}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onClick}
        style={{
          ...ui.secondaryButton,
          minHeight: 34,
          padding: "0 10px",
        }}
      >
        {buttonLabel}
      </button>
    </div>
  )
}

function MoneyLine({ label, value, tone }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "8px 0",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 800,
          color: "#64748b",
        }}
      >
        {label}
      </span>

      <strong
        style={{
          fontSize: 13,
          fontWeight: 950,
          color: tone,
        }}
      >
        {value}
      </strong>
    </div>
  )
}

function CapacityMiniCard({ label, value, tone }) {
  const selected =
    tone === "danger"
      ? {
          bg: "#fef2f2",
          border: "#fecaca",
          color: "#b91c1c",
        }
      : {
          bg: "#f0fdf4",
          border: "#bbf7d0",
          color: "#166534",
        }

  return (
    <div
      style={{
        padding: 11,
        borderRadius: 15,
        background: selected.bg,
        border: `1px solid ${selected.border}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 850,
          color: "#64748b",
          marginBottom: 4,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          fontWeight: 950,
          color: selected.color,
          letterSpacing: "-0.04em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  )
}

function getItemTitle(item, fallback) {
  return (
    item?.title ||
    item?.label ||
    item?.name ||
    item?.patientName ||
    item?.doctorName ||
    item?.clinicName ||
    fallback
  )
}

function getItemDescription(item) {
  return (
    item?.message ||
    item?.description ||
    item?.helper ||
    item?.subtitle ||
    item?.reason ||
    ""
  )
}

function getItemTone(item) {
  const severity = String(item?.severity || item?.level || item?.priority || "").toUpperCase()

  if (["HIGH", "CRITICAL", "DANGER", "URGENT"].includes(severity)) {
    return {
      bg: "#fef2f2",
      border: "#fecaca",
      color: "#b91c1c",
    }
  }

  if (["MEDIUM", "WARNING", "WARN"].includes(severity)) {
    return {
      bg: "#fff7ed",
      border: "#fed7aa",
      color: "#c2410c",
    }
  }

  return {
    bg: "#f8fafc",
    border: "#e2e8f0",
    color: "#0f172a",
  }
}
}